import type { BookingParty, ShipmentStatus } from '@/types'

/** Matches Courier::SLUGS on the backend and what booking writes to orders.carrier. */
export const POS_LAJU = 'POS LAJU'

export const SHIPMENT_STATUS_LABELS: Record<ShipmentStatus, string> = {
  booked: 'Booked',
  picked_up: 'Picked up',
  in_transit: 'In transit',
  out_for_delivery: 'Out for delivery',
  delivered: 'Delivered',
  failed: 'Delivery failed',
  returning: 'Returning to sender',
  returned: 'Returned',
  cancelled: 'Cancelled',
  exception: 'Needs attention',
}

/** Nothing further happens after these. `failed` is not here: the courier re-attempts. */
export const SHIPMENT_TERMINAL: ShipmentStatus[] = ['delivered', 'returned', 'cancelled']

export function isShipmentActive(shipment?: { status: ShipmentStatus } | null): boolean {
  return Boolean(shipment) && !SHIPMENT_TERMINAL.includes(shipment!.status)
}

/**
 * Poll an order page while its parcel is moving so webhook-driven changes
 * show up without a reload. 60 s is the useNavBadges cadence; false stops
 * the timer once the parcel is delivered, returned or cancelled.
 */
export function shipmentRefetchInterval(shipment?: { status: ShipmentStatus } | null): number | false {
  return isShipmentActive(shipment) ? 60_000 : false
}

/* The carrier's own limits, enforced on both sides. Change together with ConsignmentBuilder. */
export const DESCRIPTION_MIN = 20
export const DESCRIPTION_MAX = 50
export const WEIGHT_MIN_KG = 0.1
export const WEIGHT_MAX_KG = 30

/**
 * Client-side fallback for a carrier reason the backend could not key to a
 * field ("receiver.address.postcode does not match state" names the postcode).
 * Returns the form field, or null to leave the reason in the general list.
 */
export function bookingFieldFor(reason: string): string | null {
  const text = reason.toLowerCase()
  if (text.includes('postcode') || text.includes('post code')) return 'receiver.postcode'
  if (text.includes('phone') || text.includes('mobile')) return 'receiver.phone'
  if (text.includes('state')) return 'receiver.state'
  if (text.includes('city')) return 'receiver.city'
  if (text.includes('address') || text.includes('line')) return 'receiver.line1'
  if (text.includes('name')) return 'receiver.recipient_name'
  if (text.includes('weight')) return 'weight_kg'
  if (text.includes('description') || text.includes('content') || text.includes('item')) return 'item_description'
  return null
}

/** "line1, line2, postcode city, state" - the shape the Delivery card prints. */
export function partyLine(party: BookingParty): string {
  return [party.line1, party.line2, `${party.postcode} ${party.city}`.trim(), party.state].filter(Boolean).join(', ')
}
